import { call } from '@rental/contracts/client'
import {
  BrowserClient,
  browserSessionIntegration,
  captureException,
  dedupeIntegration,
  defaultStackParser,
  globalHandlersIntegration,
  linkedErrorsIntegration,
  setCurrentClient,
  withScope,
} from '@sentry/browser'
import { api, ProblemError } from './api'
import { reportsFromEnvelope } from './telemetry-report'

/**
 * S2: the staff app's errors and sessions (PRD:53), sent to our own API, never
 * to the provider: the SDK's transport is ours (telemetry-report.ts), posting
 * through the generated client. An explicit BrowserClient, not `init()`, so the
 * default integrations (breadcrumbs, HTTP context…) are not even in the bundle
 * (scripts/check-bundle.mjs). Always started: the server answers 202 and keeps
 * nothing while reporting is PENDING, and boot errors are caught too.
 */

/** Never contacted: the server decides where reports go. The SDK needs a valid one. */
const PLACEHOLDER_DSN = 'https://intake@client-errors.invalid/1'

/** `staff@<version>+<commit>`, so a tab left open across a deploy can be told apart. */
export const RELEASE = __BUILD_ID__
  ? `staff@${__APP_VERSION__}+${__BUILD_ID__}`
  : `staff@${__APP_VERSION__}`

let started = false
/**
 * Which report an error is, or will be, for the error screen's reference. The
 * router renders its error screen before it calls onCatch, which reports; so
 * whichever comes first picks the id, and the other uses it.
 */
const references = new WeakMap<object, string>()
const newEventId = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('')

export function startTelemetry(): void {
  if (started) return
  started = true
  const client = new BrowserClient({
    dsn: PLACEHOLDER_DSN,
    release: RELEASE,
    stackParser: defaultStackParser,
    transport: () => ({
      send: async (envelope) => {
        const body = reportsFromEnvelope(envelope)
        if (body.events.length + body.sessions.length === 0) return {}
        // Never throws: a failed report must not become another error.
        const result = await call(api.POST('/v1/client-errors', { body }))
        return result.ok ? { statusCode: 202 } : {}
      },
      flush: async () => true,
    }),
    integrations: [
      globalHandlersIntegration(),
      linkedErrorsIntegration(),
      dedupeIntegration(),
      // 'page' (SDK 11's default; 10's is 'route', a session per navigation).
      browserSessionIntegration({ lifecycle: 'page' }),
    ],
    // The API reports its own failures; offline and 4xx answers are not bugs.
    beforeSend: (event, hint) => (hint.originalException instanceof ProblemError ? null : event),
    beforeBreadcrumb: () => null,
    maxBreadcrumbs: 0,
    sendClientReports: false,
    sendDefaultPii: false,
  })
  setCurrentClient(client)
  client.init()
}

export interface HowCaught {
  /** 'router.error-boundary', 'react.uncaught', 'react-query'… */
  mechanism: string
  /** false: the screen was lost to it, a crash for PRD:53's rate. */
  handled: boolean
  /** The route's id (`/bookings/$code`), never its URL. */
  route?: string
}

/** Reports an error the app caught; returns its reference, or nothing. */
export function reportError(error: unknown, how: HowCaught): string | undefined {
  if (!started || error instanceof ProblemError) return undefined
  const chosen = referenceFor(error)
  const id = withScope((scope) => {
    if (how.route) scope.setTag('route', how.route)
    return captureException(error, {
      mechanism: { type: how.mechanism, handled: how.handled },
      ...(chosen ? { event_id: chosen } : {}),
    })
  })
  if (typeof error === 'object' && error !== null) references.set(error, id)
  return id
}

/** The reference an error is reported under, or will be: the error screen shows it. */
export function referenceFor(error: unknown): string | undefined {
  if (!started || error instanceof ProblemError) return undefined
  if (typeof error !== 'object' || error === null) return undefined
  let id = references.get(error)
  if (!id) {
    id = newEventId()
    references.set(error, id)
  }
  return id
}
